(() => {
  const root = document.documentElement;
  root.classList.add('fluffy-agency-ui');

  const initializeCollectionDescriptions = () => {
    document.querySelectorAll('[data-collection-description]').forEach((description) => {
      const content = description.querySelector('.collection-hero__description-content');
      const toggle = description.querySelector('.collection-hero__description-toggle');
      if (!content || !toggle) return;

      description.classList.add('fluffy-description-ready');

      const updateOverflow = () => {
        if (description.classList.contains('is-expanded')) return;
        toggle.hidden = content.scrollHeight <= content.clientHeight + 2;
      };

      toggle.addEventListener('click', () => {
        const expanded = !description.classList.contains('is-expanded');
        description.classList.toggle('is-expanded', expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.querySelector('[data-label-open]').hidden = expanded;
        toggle.querySelector('[data-label-close]').hidden = !expanded;
      });

      requestAnimationFrame(updateOverflow);
      if ('ResizeObserver' in window) {
        new ResizeObserver(updateOverflow).observe(content);
      }
    });
  };

  const initializeProductTone = () => {
    const productPage = document.querySelector('main[data-template="product"]');
    if (!productPage) return;

    const toneLayer = document.createElement('span');
    toneLayer.className = 'fluffy-product-tone';
    toneLayer.setAttribute('aria-hidden', 'true');
    productPage.prepend(toneLayer);

    const colorTones = [
      [/\b(ivory|cream|beige|natural|oat|linen|sand)\b/i, [216, 201, 177]],
      [/\b(white|snow|pearl)\b/i, [220, 225, 226]],
      [/\b(gray|grey|silver|charcoal)\b/i, [155, 166, 174]],
      [/\b(blue|aqua|sky)\b/i, [135, 181, 224]],
      [/\b(navy|indigo)\b/i, [72, 91, 122]],
      [/\b(pink|blush|rose)\b/i, [222, 176, 190]],
      [/\b(peach|coral)\b/i, [231, 181, 150]],
      [/\b(yellow|gold|mustard)\b/i, [226, 200, 116]],
      [/\b(green|sage|olive|mint)\b/i, [151, 183, 157]],
      [/\b(purple|lavender|lilac|plum)\b/i, [181, 158, 205]],
      [/\b(brown|tan|camel|mocha)\b/i, [164, 127, 96]],
      [/\b(red|ruby|burgundy)\b/i, [185, 117, 122]],
      [/\b(black|onyx)\b/i, [70, 72, 76]],
    ];

    const getSelectedColor = () => {
      const selects = [...productPage.querySelectorAll('variant-selects select')];
      const colorSelect = selects.find((select) => {
        const label = productPage.querySelector(`label[for="${select.id}"]`);
        return /colou?r/i.test(`${select.name} ${label?.textContent || ''}`);
      });
      if (colorSelect) return colorSelect.value;

      const checkedInputs = [
        ...productPage.querySelectorAll('variant-selects input[type="radio"]:checked'),
      ];
      const colorInput = checkedInputs.find((input) =>
        /colou?r/i.test(`${input.name} ${input.dataset.optionName || ''}`)
      );
      return colorInput?.value || '';
    };

    const updateTone = () => {
      const selectedColor = getSelectedColor();
      const match = colorTones.find(([pattern]) => pattern.test(selectedColor));
      const rgb = match?.[1] || [171, 184, 194];
      const rgbValue = rgb.join(' ');
      productPage.style.setProperty('--product-tone-rgb', rgbValue);
      toneLayer.style.backgroundColor = `rgb(${rgb.join(', ')})`;
    };

    productPage.addEventListener('change', (event) => {
      if (event.target.closest('variant-selects')) requestAnimationFrame(updateTone);
    });
    updateTone();
  };

  const initializeProductAppFallbacks = () => {
    const productInfo = document.querySelector('.product__info-container');
    const signals = productInfo?.querySelector('[data-fluffy-purchase-signals]');
    if (!productInfo || !signals) return;

    const viewerFallback = signals.querySelector('[data-fluffy-viewer-fallback]');
    const deliveryFallback = signals.querySelector('[data-fluffy-delivery-fallback]');

    const syncAppSignals = () => {
      const viewerCounter = productInfo.querySelector('#effectiveAppsProductVisitorsCounterElement');
      const deliveryWidget = productInfo.querySelector('.delivery-widget__auto');
      const viewerText = viewerCounter?.textContent.trim() || '';
      const deliveryText = deliveryWidget?.textContent.trim() || '';
      const hasViewerContent = /\d+\s+people\s+looked at this product/i.test(viewerText);
      const hasDeliveryContent = Boolean(
        deliveryWidget?.querySelector('.cutoff-time') &&
          deliveryWidget.querySelectorAll('.step-box__basic').length >= 3 &&
          /order ready/i.test(deliveryText) &&
          /delivered/i.test(deliveryText)
      );

      if (viewerFallback) {
        viewerFallback.hidden = hasViewerContent;
      }
      if (deliveryFallback) {
        deliveryFallback.hidden = hasDeliveryContent;
      }

      viewerCounter?.classList.add('fluffy-native-app-signal');
      viewerCounter?.classList.toggle('fluffy-native-app-signal--ready', hasViewerContent);
      deliveryWidget?.classList.add('fluffy-native-app-delivery');
      deliveryWidget?.classList.toggle('fluffy-native-app-delivery--ready', hasDeliveryContent);
    };

    syncAppSignals();

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(syncAppSignals);
      observer.observe(productInfo, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 12000);
    }
  };

  initializeCollectionDescriptions();
  initializeProductTone();
  initializeProductAppFallbacks();

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    root.classList.add('fluffy-reduced-motion');
    return;
  }

  root.classList.add('fluffy-motion-ready');

  const revealTargets = document.querySelectorAll(
    '[data-fluffy-reveal], main[data-template="index"] > .shopify-section'
  );

  if (!('IntersectionObserver' in window)) {
    revealTargets.forEach((target) => target.classList.add('fluffy-is-visible'));
    return;
  }

  const revealObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('fluffy-is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  revealTargets.forEach((target, index) => {
    target.style.setProperty('--fluffy-reveal-order', String(index % 4));
    revealObserver.observe(target);
  });
})();
